import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { ApiService } from '../services/apiService';
import { formatTournamentDateTime, bracketPlayerLabel, tournamentRoundLabel, isSwissFormat, computeSwissStandings, formatSwissPoints, MAX_TOURNAMENT_PLAYERS } from '../utils/tournamentUtils';
import { DEFAULT_ELO, eloPoolLabel, getTournamentSeedPool } from '../utils/eloUtils';
import PageCloseButton from './PageCloseButton';
import './TournamentPage.css';

interface Tournament {
  id: number;
  name: string;
  status: 'scheduled' | 'registration' | 'in_progress' | 'complete' | 'cancelled';
  bracket_size: number;
  player_count: number;
  current_round: number;
  total_rounds: number;
  registration_open: number | boolean;
  created_at: string;
  scheduled_start_at?: string | null;
  is_rated?: number | boolean;
  auto_start?: number | boolean;
  format?: 'single_elim' | 'swiss';
}

interface Participant {
  user_id: number;
  username: string;
  seed: number;
  status: string;
  elo_rating: number;
  elo_fast?: number;
  elo_standard?: number;
}

interface TMatch {
  id: number;
  round: number;
  match_number: number;
  player1_id: number | null;
  player2_id: number | null;
  winner_id: number | null;
  status: string;
  game_id?: string | null;
  player1_username?: string;
  player2_username?: string;
  winner_username?: string;
}

interface Props {
  socket: Socket | null;
  userId: number | string | null;
  username: string | null;
  isGuest?: boolean;
  onCreateAccount?: () => void;
  onClose: () => void;
  onWatchGame?: (gameId: string, context?: { tournamentName: string; round: number }) => void;
}

const STATUS_COLORS: Record<string, string> = {
  scheduled: '#e67e22',
  registration: '#3498db',
  in_progress: '#27ae60',
  complete: '#9b59b6',
  cancelled: '#7f8c8d'
};

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Coming Soon',
  registration: 'Registration Open',
  in_progress: 'In Progress',
  complete: 'Complete',
  cancelled: 'Cancelled'
};

const isPastTournament = (t: Tournament) => t.status === 'complete' || t.status === 'cancelled';

const MATCH_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  ready_check: 'Ready Check',
  in_progress: 'In Progress',
  complete: 'Complete',
  walkover: 'Walkover',
  double_forfeit: 'Double Forfeit',
  draw: 'Draw',
  bye: 'Win (Bye)'
};


const TournamentPage: React.FC<Props> = ({ socket, userId, username, isGuest, onCreateAccount, onClose, onWatchGame }) => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selected, setSelected] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [matches, setMatches] = useState<TMatch[]>([]);
  const [roundSettings, setRoundSettings] = useState<Array<{ minutes_per_player?: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 5000);
  };

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ApiService.getActiveTournaments();
      if (!result.error) setTournaments(result.tournaments || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const loadTournament = useCallback(async (id: number) => {
    try {
      const result = await ApiService.getTournament(id);
      if (!result.error && result.tournament) {
        setSelected(result.tournament);
        setParticipants(result.participants || []);
        setMatches(result.matches || []);
        setRoundSettings(result.roundSettings || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  // Socket events
  useEffect(() => {
    if (!socket) return;

    const onUpdated = ({ tournamentId }: { tournamentId: number }) => {
      loadTournaments();
      if (selected?.id === tournamentId) loadTournament(tournamentId);
    };

    const onMatchResult = (data: { matchId: number; tournamentId: number; result?: string; reason?: string }) => {
      if (selected?.id === data.tournamentId) loadTournament(data.tournamentId);
    };

    const onTournamentStarted = (data: { tournamentId: number; tournamentName: string }) => {
      showToast(`Tournament "${data.tournamentName}" has started!`);
      loadTournaments();
    };

    const onCompleted = (data: { tournamentId: number; winnerUsername: string }) => {
      showToast(`Tournament complete! Winner: ${data.winnerUsername}`);
      loadTournaments();
      if (selected?.id === data.tournamentId) loadTournament(data.tournamentId);
    };

    socket.on('tournament:updated', onUpdated);
    socket.on('tournament:matchResult', onMatchResult);
    socket.on('tournament:started', onTournamentStarted);
    socket.on('tournament:completed', onCompleted);

    return () => {
      socket.off('tournament:updated', onUpdated);
      socket.off('tournament:matchResult', onMatchResult);
      socket.off('tournament:started', onTournamentStarted);
      socket.off('tournament:completed', onCompleted);
    };
  }, [socket, selected, loadTournaments, loadTournament]);

  const handleSignUp = async (tournamentId: number) => {
    try {
      const result = await ApiService.signUpForTournament(tournamentId);
      if (result.error) { showToast(result.error); return; }
      showToast('Signed up!');
      loadTournaments();
      if (selected?.id === tournamentId) loadTournament(tournamentId);
    } catch { showToast('Failed to sign up'); }
  };

  const handleWithdraw = async (tournamentId: number) => {
    try {
      const result = await ApiService.withdrawFromTournament(tournamentId);
      if (result.error) { showToast(result.error); return; }
      showToast('Withdrawn from tournament');
      loadTournaments();
      if (selected?.id === tournamentId) loadTournament(tournamentId);
    } catch { showToast('Failed to withdraw'); }
  };

  const numericUserId = userId !== null ? Number(userId) : null;

  const canSignUp = (t: Tournament) =>
    !!t.registration_open && (t.status === 'registration' || t.status === 'scheduled');

  const canWithdraw = (t: Tournament) =>
    t.status === 'registration' || t.status === 'scheduled';

  const isTournamentRated = (t: Tournament) => t.is_rated === 1 || t.is_rated === true;
  const isTournamentAutoStart = (t: Tournament) => t.auto_start === 1 || t.auto_start === true;

  const roundsArray = selected && selected.total_rounds > 0
    ? Array.from({ length: selected.total_rounds }, (_, i) => i + 1)
    : [];

  const liveWatchable = selected
    ? matches.filter(m =>
        m.status === 'in_progress' &&
        !!m.game_id &&
        !!onWatchGame &&
        m.player1_id !== numericUserId &&
        m.player2_id !== numericUserId
      )
    : [];

  const currentTournaments = tournaments.filter(t => !isPastTournament(t));
  const pastTournaments = tournaments.filter(isPastTournament);

  const openTournament = (t: Tournament) => {
    setSelected(t);
    loadTournament(t.id);
  };

  const renderTournamentCard = (t: Tournament) => (
    <div key={t.id} className="tp-card" onClick={() => openTournament(t)}>
      <div className="tp-card-top">
        <span className="tp-card-name">{t.name}</span>
        <span className="tp-status-badge" style={{ background: STATUS_COLORS[t.status] }}>
          {STATUS_LABELS[t.status] || t.status}
        </span>
      </div>
      <div className="tp-card-meta">
        <span>{t.player_count} / {MAX_TOURNAMENT_PLAYERS} player{t.player_count !== 1 ? 's' : ''}</span>
        <span>{t.format === 'swiss' ? 'Swiss' : 'Bracket'}</span>
        {isTournamentRated(t) && <span className="tp-rated-tag">Rated</span>}
        {isTournamentAutoStart(t) && <span className="tp-autostart-tag">Auto-start</span>}
        {t.scheduled_start_at && (
          <span>Starts {formatTournamentDateTime(t.scheduled_start_at)}</span>
        )}
        {t.status === 'in_progress' && (
          <span>{tournamentRoundLabel(t.current_round, t.total_rounds, t.format)}</span>
        )}
        {canSignUp(t) && (
          <span className="tp-reg-open">Registration Open</span>
        )}
      </div>
      <div className={`tp-card-cta${t.status === 'in_progress' ? ' tp-card-cta-watch' : ''}`}>
        {t.status === 'in_progress'
          ? 'Watch live games →'
          : t.format === 'swiss' ? 'View standings →' : 'View Bracket →'}
      </div>
    </div>
  );

  return (
    <div className="tp-container">
      {toast && <div className="tp-toast">{toast}</div>}

      <div className="tp-header">
        <h2>Tournaments</h2>
        <div className="tp-header-actions">
          {selected && (
            <button className="tp-back-btn" onClick={() => { setSelected(null); setParticipants([]); setMatches([]); }}>
              ← All Tournaments
            </button>
          )}
          <PageCloseButton onClick={onClose} ariaLabel="Close tournaments" />
        </div>
      </div>

      {loading && <div className="tp-loading">Loading...</div>}

      {isGuest && (
        <div className="tp-guest-banner" role="status">
          <div className="tp-guest-banner-copy">
            <strong>Only members can register and play in tournaments.</strong>
            <span>Create a username and password to register.</span>
            <span>You can still watch tournament games without an account.</span>
            <span>Open an in-progress event, then tap Watch on a live game.</span>
          </div>
          {onCreateAccount && (
            <button type="button" className="tp-guest-banner-btn" onClick={onCreateAccount}>
              Create account
            </button>
          )}
        </div>
      )}

      {/* Tournament list */}
      {!selected && (
        <>
          {currentTournaments.length === 0 && !loading && (
            <div className={`tp-empty${pastTournaments.length > 0 ? ' tp-empty-compact' : ''}`}>
              {pastTournaments.length > 0
                ? 'No upcoming or live tournaments.'
                : 'No tournaments to show. Check back soon.'}
            </div>
          )}
          {currentTournaments.length > 0 && (
            <div className="tp-list">
              {currentTournaments.map(renderTournamentCard)}
            </div>
          )}
          {pastTournaments.length > 0 && (
            <div className="tp-past">
              <button
                type="button"
                className="tp-past-toggle"
                aria-expanded={showPast}
                onClick={() => setShowPast(open => !open)}
              >
                <span>{showPast ? '▾' : '▸'} Past tournaments ({pastTournaments.length})</span>
              </button>
              {showPast && (
                <div className="tp-list tp-past-list">
                  {pastTournaments.map(renderTournamentCard)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Tournament detail */}
      {selected && (
        <div className="tp-detail">
          <div className="tp-detail-header">
            <span className="tp-status-badge" style={{ background: STATUS_COLORS[selected.status], fontSize: '0.85rem' }}>
              {STATUS_LABELS[selected.status] || selected.status}
            </span>
            {isTournamentRated(selected) && <span className="tp-rated-tag">Rated</span>}
            {isSwissFormat(selected) && <span className="tp-rated-tag">Swiss</span>}
            {!isSwissFormat(selected) && <span className="tp-rated-tag">Bracket</span>}
            {selected.status === 'in_progress' && (
              <span className="tp-round-indicator">{tournamentRoundLabel(selected.current_round, selected.total_rounds, selected.format)}</span>
            )}
          </div>

          {selected.status === 'in_progress' && liveWatchable.length > 0 && (
            <div className="tp-live-now">
              <h3>Live now</h3>
              <p className="tp-live-now-note">Tap Watch to follow a game.</p>
              <div className="tp-live-now-list">
                {liveWatchable.map(m => (
                  <div key={m.id} className="tp-live-now-row">
                    <div className="tp-live-now-match">
                      <div className="tp-live-now-names">
                        {bracketPlayerLabel(m.player1_username, m.player1_id, m.status)}
                        {' vs '}
                        {bracketPlayerLabel(m.player2_username, m.player2_id, m.status)}
                      </div>
                      <div className="tp-live-now-round">
                        {isSwissFormat(selected) ? `Round ${m.round}` : tournamentRoundLabel(m.round, selected.total_rounds, selected.format)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="tp-watch-btn tp-watch-btn-lg"
                      disabled={!socket?.connected}
                      title={socket?.connected ? 'Watch this game' : 'Connecting…'}
                      onClick={() => onWatchGame?.(m.game_id!, { tournamentName: selected.name, round: m.round })}
                    >
                      Watch
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected.scheduled_start_at && (canSignUp(selected) || canWithdraw(selected)) && (
            <div className="tp-scheduled-banner">
              {isTournamentAutoStart(selected) ? (
                <>
                  Tournament auto-starts{' '}
                  <strong>{formatTournamentDateTime(selected.scheduled_start_at) || 'at the scheduled time'}</strong>.
                  {' '}Registration closes then, and Round 1 begins automatically.
                </>
              ) : (
                <>
                  Registration closes{' '}
                  <strong>{formatTournamentDateTime(selected.scheduled_start_at) || 'at the scheduled time'}</strong>.
                  {' '}An admin will start the tournament and open each round.
                </>
              )}
            </div>
          )}

          {isTournamentRated(selected) && canSignUp(selected) && (
            <div className="tp-rated-banner" role="status">
              This is a <strong>rated {eloPoolLabel(getTournamentSeedPool(roundSettings)) || 'Standard'}</strong> tournament.
              Finished Igo/Wego games count toward that rating. Resign, timeout, and disconnect count only if both players have made at least one Yugo.
            </div>
          )}

          {/* Sign-up / withdraw */}
          {numericUserId && (canSignUp(selected) || (canWithdraw(selected) && participants.some(p => p.user_id === numericUserId))) && (
            <div className="tp-signup-row">
              {!participants.some(p => p.user_id === numericUserId) ? (
                canSignUp(selected) ? (
                  participants.length >= MAX_TOURNAMENT_PLAYERS ? (
                    <div className="tp-signed-up">This tournament is full ({MAX_TOURNAMENT_PLAYERS} players).</div>
                  ) : (
                    <button className="tp-signup-btn" onClick={() => handleSignUp(selected.id)}>
                      Sign Up for This Tournament
                    </button>
                  )
                ) : null
              ) : (
                <div className="tp-registered-actions">
                  <div className="tp-signed-up">✓ You are registered</div>
                  {canWithdraw(selected) && (
                    <button className="tp-withdraw-btn" onClick={() => handleWithdraw(selected.id)}>
                      Withdraw
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Player list — no projected pairings during signup */}
          <div className="tp-players-section">
            <h3>Players ({participants.length} / {MAX_TOURNAMENT_PLAYERS})</h3>
            {(selected.status === 'scheduled' || selected.status === 'registration') && (
              <div className="tp-bracket-preview-note">
                Showing {eloPoolLabel(getTournamentSeedPool(roundSettings)) || 'Standard'} Elo
                {roundSettings.length === 0 ? ' (Standard until round clocks are set)' : ''}.
                Pairings are set when the tournament starts.
              </div>
            )}
            <div className="tp-players-grid">
              {(() => {
                const setupPhase = selected.status === 'scheduled' || selected.status === 'registration';
                const pool = getTournamentSeedPool(roundSettings);
                const listed = setupPhase
                  ? [...participants].sort((a, b) => (a.username || '').localeCompare(b.username || '', undefined, { sensitivity: 'base' }))
                  : participants;
                return listed.map((p, i) => {
                  const elo = pool === 'fast'
                    ? (p.elo_fast ?? p.elo_rating ?? DEFAULT_ELO)
                    : (p.elo_standard ?? p.elo_rating ?? DEFAULT_ELO);
                  return (
                <div key={p.user_id} className={`tp-player-chip ${p.user_id === numericUserId ? 'tp-player-me' : ''}`}>
                  {!setupPhase && <span className="tp-player-seed">{p.seed || i + 1}</span>}
                  <span className="tp-player-name">{p.username}</span>
                  <span className="tp-player-elo">{Math.round(elo)}</span>
                  {p.status === 'eliminated' && <span className="tp-elim">✗</span>}
                  {p.status === 'winner' && <span className="tp-won">🏆</span>}
                </div>
                  );
                });
              })()}
            </div>
          </div>

          {isSwissFormat(selected) && (selected.status === 'in_progress' || selected.status === 'complete') && participants.length > 0 && (
            <div className="tp-players-section">
              <h3>Standings</h3>
              <table className="tp-standings">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th>Pts</th>
                    <th>Buchholz</th>
                  </tr>
                </thead>
                <tbody>
                  {computeSwissStandings(participants, matches).map((row, i) => (
                    <tr key={row.user_id} className={row.user_id === numericUserId ? 'tp-standings-me' : ''}>
                      <td>{i + 1}</td>
                      <td>{row.username}{row.user_id === numericUserId ? ' (you)' : ''}</td>
                      <td>{formatSwissPoints(row.points)}</td>
                      <td>{formatSwissPoints(row.buchholz)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bracket / pairings */}
          {matches.length > 0 && (
            <div className="tp-bracket-section">
              <h3>{isSwissFormat(selected) ? 'Pairings' : 'Bracket'}</h3>
              <div className="tp-bracket">
                {roundsArray.map(round => {
                  const roundMatches = matches.filter(m => m.round === round);
                  if (roundMatches.length === 0) return null;
                  return (
                    <div key={round} className="tp-bracket-round">
                      <div className="tp-bracket-round-label">
                        {isSwissFormat(selected)
                          ? `Round ${round}`
                          : (round === selected.total_rounds ? 'Final' : round === selected.total_rounds - 1 ? 'Semi-Final' : `Round ${round}`)}
                      </div>
                      <div className="tp-bracket-matches">
                        {roundMatches.map(m => {
                          const myMatch = m.player1_id === numericUserId || m.player2_id === numericUserId;
                          return (
                            <div key={m.id} className={`tp-bracket-match ${myMatch ? 'tp-my-match' : ''} tp-bm-${m.status}`}>
                              <div className={`tp-bm-player ${m.winner_id === m.player1_id && m.winner_id ? 'tp-bm-winner' : ''}`}>
                                {bracketPlayerLabel(m.player1_username, m.player1_id, m.status)}
                                {m.player1_id === numericUserId && <span className="tp-you-tag"> (you)</span>}
                              </div>
                              <div className="tp-bm-divider" />
                              <div className={`tp-bm-player ${m.winner_id === m.player2_id && m.winner_id ? 'tp-bm-winner' : ''}`}>
                                {bracketPlayerLabel(m.player2_username, m.player2_id, m.status)}
                                {m.player2_id === numericUserId && <span className="tp-you-tag"> (you)</span>}
                              </div>
                              <div className="tp-bm-status">
                                <span className={`tp-bm-status-label tp-bml-${m.status}`}>
                                  {MATCH_STATUS_LABELS[m.status] || m.status}
                                </span>
                                {m.status === 'in_progress' && m.game_id && onWatchGame && !myMatch && (
                                  <button
                                    className="tp-watch-btn"
                                    disabled={!socket?.connected}
                                    title={socket?.connected ? 'Watch this game' : 'Connecting…'}
                                    onClick={() => onWatchGame(m.game_id!, { tournamentName: selected.name, round: m.round })}
                                  >
                                    👁 Watch
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selected.status === 'complete' && (() => {
            if (isSwissFormat(selected)) {
              const leader = computeSwissStandings(participants, matches)[0];
              return leader?.username ? (
                <div className="tp-winner-banner">
                  🏆 Tournament Champion: <strong>{leader.username}</strong>
                </div>
              ) : null;
            }
            const finalMatch = matches.find(m => m.round === selected.total_rounds);
            return finalMatch?.winner_username ? (
              <div className="tp-winner-banner">
                🏆 Tournament Champion: <strong>{finalMatch.winner_username}</strong>
              </div>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
};

export default TournamentPage;
